import type { WikiPageView } from "@esi/shared";
import { ShieldCheck, TriangleAlert, UserRound } from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ApiError } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import { t as translate } from "../i18n/i18n.ts";
import { useMembers } from "../projects/queries.ts";
import { usePageMetadata } from "./queries.ts";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days since `iso` (never negative). */
export function daysSince(iso: string, now = Date.now()): number {
  return Math.max(0, Math.floor((now - Date.parse(iso)) / DAY_MS));
}

/**
 * Why the page reads "May be stale" (spec `project-wiki` "Stale pages are flagged"), in words:
 * a source changed on a date, or the review period passed since its verification or publication.
 */
export function staleReason(page: WikiPageView): string | null {
  const stale = page.stale;
  if (!stale) {
    return null;
  }
  if (stale.reason === "source_changed") {
    return translate("wiki.stewardship.stale.sourceChanged", { date: formatDate(stale.since) });
  }
  const count = daysSince(stale.since);
  return page.verification?.at === stale.since
    ? translate("wiki.stewardship.stale.lastVerified", { count })
    : translate("wiki.stewardship.stale.published", { count });
}

/** A refused owner or verification change, in plain words that name no hidden source. */
export function stewardshipRefusal(error: Error): string {
  if (!(error instanceof ApiError)) {
    return error.message;
  }
  if (error.status === 404) {
    return translate("wiki.stewardship.refused.notFound");
  }
  if (error.status === 403) {
    return translate("wiki.stewardship.refused.forbidden");
  }
  if (error.status === 409 && error.code === "project_frozen") {
    return translate("wiki.stewardship.refused.frozen");
  }
  if (error.status === 409) {
    return translate("wiki.stewardship.refused.newer");
  }
  return error.message;
}

/**
 * The rail's owner, verification and staleness (wiki-okf-structure slice B). Each is a quiet
 * icon-and-text line; verification and staleness are state signals, so they carry their state
 * colour with a word. Actions appear only when the API says the caller may take them.
 */
export function PageStewardship({ projectId, page }: { projectId: string; page: WikiPageView }) {
  const { t } = useTranslation();
  const change = usePageMetadata(projectId, page.id);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const ownerTrigger = useRef<HTMLButtonElement>(null);
  const reason = staleReason(page);
  const can = page.can;
  return (
    <section
      data-esi-content="wiki"
      className="flex flex-col gap-3"
      aria-labelledby={`stewardship-${page.id}`}
    >
      <h2 id={`stewardship-${page.id}`} className="text-xs font-semibold text-fg">
        {t("wiki.stewardship.title")}
      </h2>
      <div className="flex flex-col gap-2.5 text-fg-secondary">
        <p className="flex items-start gap-2">
          <UserRound aria-hidden className="mt-px size-3.5 shrink-0" strokeWidth={1.75} />
          <span className="min-w-0 break-words">
            {page.owner
              ? t("wiki.stewardship.owner", {
                  name: page.owner.name ?? t("wiki.stewardship.someone"),
                })
              : t("wiki.stewardship.noOwner")}
          </span>
        </p>
        {page.verification ? (
          <p className="flex items-start gap-2 text-success-fg">
            <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0" strokeWidth={1.75} />
            <span className="min-w-0 break-words">
              {t("wiki.stewardship.verified", {
                name: page.verification.by.name ?? t("wiki.stewardship.someone"),
                date: formatDate(page.verification.at),
              })}
            </span>
          </p>
        ) : (
          <p className="flex items-start gap-2">
            <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0" strokeWidth={1.75} />
            <span>{t("wiki.stewardship.notVerified")}</span>
          </p>
        )}
        {reason ? (
          <div className="flex items-start gap-2 text-warning-fg">
            <TriangleAlert aria-hidden className="mt-px size-3.5 shrink-0" strokeWidth={1.75} />
            <p className="min-w-0 break-words">
              <span className="font-medium">{t("wiki.stewardship.stale.label")}</span>
              <span className="block text-fg-secondary">{reason}</span>
            </p>
          </div>
        ) : null}
      </div>
      {can && (can.verify || can.withdrawVerification || can.setOwner) ? (
        <div className="flex flex-col items-start gap-1">
          {can.verify ? (
            <Button
              size="xs"
              variant="outline"
              disabled={change.isPending}
              onClick={() => change.mutate({ kind: "verify", version: page.shownVersion })}
            >
              {t("wiki.stewardship.verify")}
            </Button>
          ) : null}
          {can.withdrawVerification ? (
            <Button
              size="xs"
              variant="ghost"
              // Ghost text lines up with the lines above it.
              className="-ml-2"
              disabled={change.isPending}
              onClick={() => change.mutate({ kind: "withdraw" })}
            >
              {t("wiki.stewardship.withdraw")}
            </Button>
          ) : null}
          {can.setOwner ? (
            <Button
              ref={ownerTrigger}
              size="xs"
              variant="ghost"
              // Ghost text lines up with the lines above it.
              className="-ml-2"
              disabled={change.isPending}
              onClick={() => {
                change.reset();
                setOwnerOpen(true);
              }}
            >
              {t("wiki.stewardship.changeOwner")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {change.isError && !ownerOpen ? (
        <p role="alert" className="text-danger-fg">
          {stewardshipRefusal(change.error)}
        </p>
      ) : null}
      <OwnerDialog
        open={ownerOpen}
        projectId={projectId}
        page={page}
        change={change}
        onClose={() => setOwnerOpen(false)}
        returnFocus={() => ownerTrigger.current}
      />
    </section>
  );
}

/** "Change owner": one native select of the project's members, or nobody. */
function OwnerDialog(props: {
  open: boolean;
  projectId: string;
  page: WikiPageView;
  change: ReturnType<typeof usePageMetadata>;
  onClose: () => void;
  returnFocus: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const id = useId();
  const members = useMembers(props.projectId, props.open);
  const [chosen, setChosen] = useState<string | null>(null);
  const value = chosen ?? props.page.owner?.id ?? "";
  const { change } = props;
  const close = () => {
    if (!change.isPending) {
      setChosen(null);
      change.reset();
      props.onClose();
    }
  };
  return (
    <AppDialog
      open={props.open}
      onClose={close}
      returnFocus={props.returnFocus}
      title={t("wiki.stewardship.ownerDialog.title", { title: props.page.title })}
      description={t("wiki.stewardship.ownerDialog.body")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          change.mutate(
            { kind: "owner", userId: value === "" ? null : value },
            { onSuccess: close },
          );
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={id}>{t("wiki.stewardship.ownerDialog.field")}</Label>
          <NativeSelect
            id={id}
            value={value}
            disabled={change.isPending || members.isPending}
            onChange={(event) => setChosen(event.target.value)}
          >
            <option value="">{t("wiki.stewardship.ownerDialog.nobody")}</option>
            {props.page.owner && !members.data?.some((m) => m.userId === props.page.owner?.id) ? (
              <option value={props.page.owner.id}>
                {props.page.owner.name ?? t("wiki.stewardship.someone")}
              </option>
            ) : null}
            {(members.data ?? []).map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.displayName ?? m.email ?? t("wiki.stewardship.someone")}
              </option>
            ))}
          </NativeSelect>
        </div>
        {members.isError ? (
          <Notice tone="danger">{t("wiki.stewardship.membersFailed")}</Notice>
        ) : null}
        {change.isError ? <Notice tone="danger">{stewardshipRefusal(change.error)}</Notice> : null}
        <DialogFooter>
          <Button type="button" variant="outline" disabled={change.isPending} onClick={close}>
            {t("wiki.stewardship.ownerDialog.cancel")}
          </Button>
          <Button type="submit" disabled={change.isPending}>
            {change.isPending
              ? t("wiki.stewardship.ownerDialog.saving")
              : t("wiki.stewardship.ownerDialog.save")}
          </Button>
        </DialogFooter>
      </form>
    </AppDialog>
  );
}
